import AutoDismissToolTip from "@/components/AutoDismissToolTip";
import ProxyImage from "@/components/ProxyImage";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { Form, Spin } from "antd";
import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo, useState } from "react";
import { useLinkedProductSearchFieldsStore } from "./LinkedProductSearchFieldsStore";
import Highlight from "@/components/Highlight";
import { HotProductSku } from "./type";
import {
    FlexibleSelectData,
    SelectType,
} from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect/type";
import FlexibleSelect from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect";
import useInitialDataSourceValue from "./useInitialDataSourceValue";
import { useUpdateEffect } from "ahooks";
import { difference, uniq } from "lodash";

interface SkuPropertiesFieldProps {
    shopId?: number;
}

/** 爆款模式下，tag一样表示为同一种商品 */
function buildHotProductTag(hotProductSku: HotProductSku) {
    const { originalId, skuProperties, shopId } = hotProductSku;

    return `${shopId}|${originalId}|${skuProperties}`;
}

interface SkuPropertiesFieldCoreProps
    extends SkuPropertiesFieldProps,
        Pick<TableSearchState, "orderItemTitleValue" | "skuPropertiesValue"> {}

const SKU_PROPERTIES_FIELD_MULTIPLE = "SKU_PROPERTIES_FIELD_MULTIPLE";

const SkuPropertiesFieldCore: React.FC<SkuPropertiesFieldCoreProps> = observer((props) => {
    const { orderItemTitleValue = {}, skuPropertiesValue = {}, shopId } = props;
    const orderManagementStore = useOrderManagementStore();
    const [searchSkuText, updateSearchSkuText] = useState("");
    const store = useLinkedProductSearchFieldsStore();
    const { onFocusLoad, enabledTopSellingProductPrinting, productSkus, sameSkuPropertiesMap, productSkuMap } = store;

    const handleClear = useCallback(() => {
        orderManagementStore.table.form.setFieldsValue({
            skuPropertiesValue: {
                skuProperties: "",
                skuKey: undefined,
                skuKeys: [],
                skuPropertiesFieldSkuKeys: undefined,
                isSelectAll: false,
                isIndeterminate: false,
            },
        });
    }, []);

    useUpdateEffect(() => {
        if (!orderManagementStore?.table?.form) return;

        const isSelectAll =
            skuPropertiesValue.skuKeys?.length === initialDataSourceValue.length &&
            skuPropertiesValue.skuKeys?.length > 0 &&
            initialDataSourceValue?.length > 0;

        const isIndeterminate = !isSelectAll && skuPropertiesValue.skuKeys?.length > 0;

        orderManagementStore.table.form.setFieldsValue({
            skuPropertiesValue: {
                isSelectAll,
                isIndeterminate,
            },
        });
    }, [skuPropertiesValue.skuKeys]);

    const inputTextMap = useMemo<Map<string, string>>(() => {
        const map = new Map();

        productSkuMap.forEach((it) => {
            map.set(it.skuKey, it.skuProperties);
        });

        return map;
    }, [productSkuMap]);

    function getSkuPropertiesFieldSkuKeysInHot(productSku: HotProductSku) {
        const productSkuTag = buildHotProductTag(productSku);
        const sameSkuList = productSkus.filter((it) => buildHotProductTag(it) === productSkuTag);
        const skuPropertiesFieldSkuKeys = sameSkuList.map(({ skuKey }) => skuKey) || [];

        return skuPropertiesFieldSkuKeys;
    }

    function getSkuPropertiesFieldSkuKeysNotInHot(productSku: HotProductSku) {
        /** 非爆款模式下，找到所有相同属性名称进行搜索 */
        const sameSkuPropertiesList = sameSkuPropertiesMap.get(productSku.skuProperties);
        const skuPropertiesFieldSkuKeys = [];

        let productKeys = [];

        if (orderItemTitleValue.productKeys?.length) {
            productKeys = orderItemTitleValue.productKeys;
        } else if (orderItemTitleValue.productKey) {
            productKeys = [orderItemTitleValue.productKey];
        }

        if (productKeys?.length) {
            /** 选中商品名称的情况下，仅在选中的商品的进行筛选相同的属性 */
            sameSkuPropertiesList.forEach(({ skuKey, productKey }) => {
                if (productKeys.includes(productKey)) {
                    skuPropertiesFieldSkuKeys.push(skuKey);
                }
            });
        } else {
            skuPropertiesFieldSkuKeys.push(...sameSkuPropertiesList.map(({ skuKey }) => skuKey));
        }

        return skuPropertiesFieldSkuKeys;
    }

    function handleFocusLoad() {
        onFocusLoad();

        updateSearchSkuText("");
    }

    function handleChange(skuProperties: string, skuKey: string, skuKeys: string[]) {
        if (skuKeys) {
            const skuPropertiesFieldSkuKeys = [];

            skuKeys.forEach((key) => {
                const productSku = productSkuMap.get(key);

                if (productSku) {
                    if (enabledTopSellingProductPrinting) {
                        skuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysInHot(productSku));
                    } else {
                        skuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysNotInHot(productSku));
                    }
                }
            });

            orderManagementStore.table.form.setFieldsValue({
                skuPropertiesValue: {
                    skuPropertiesFieldSkuKeys: skuPropertiesFieldSkuKeys.length ? skuPropertiesFieldSkuKeys : undefined,
                    skuProperties: "",
                    skuKeys,
                    skuKey: undefined,
                },
            });
        } else if (skuKey) {
            const productSku = productSkuMap.get(skuKey);
            const skuPropertiesFieldSkuKeys = [];

            if (productSku) {
                if (enabledTopSellingProductPrinting) {
                    /** 爆款模式下，需要找到skuOriginalId+属性都相同的群组 */
                    skuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysInHot(productSku));
                } else {
                    skuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysNotInHot(productSku));
                }
            }

            orderManagementStore.table.form.setFieldsValue({
                skuPropertiesValue: {
                    skuProperties: "",
                    skuKey,
                    skuKeys: [],
                    skuPropertiesFieldSkuKeys,
                    isIndeterminate: false,
                    isSelectAll: false,
                },
            });
        } else {
            orderManagementStore.table.form.setFieldsValue({
                skuPropertiesValue: {
                    skuKeys: [],
                    skuProperties,
                    skuKey: undefined,
                    skuPropertiesFieldSkuKeys: undefined,
                    isSelectAll: false,
                    isIndeterminate: false,
                },
            });
        }
    }

    function handleSelectAllChange(checked: boolean) {
        let nextSkuPropertiesFieldSkuKeys: string[] = [...(skuPropertiesValue.skuPropertiesFieldSkuKeys || [])];
        let nextSkuKeys: string[] = [...(skuPropertiesValue.skuKeys || [])];

        handleClear();

        if (checked) {
            for (let i = 0; i < dataSource.length; i++) {
                const skuKey = dataSource[i].value;
                const productSku = productSkuMap.get(skuKey);
                if (!productSku) continue;

                nextSkuKeys.push(skuKey);

                if (enabledTopSellingProductPrinting) {
                    nextSkuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysInHot(productSku));
                } else {
                    nextSkuPropertiesFieldSkuKeys.push(...getSkuPropertiesFieldSkuKeysNotInHot(productSku));
                }
            }

            nextSkuPropertiesFieldSkuKeys = uniq(nextSkuPropertiesFieldSkuKeys);
            nextSkuKeys = uniq(nextSkuKeys);
        } else {
            if (searchSkuText) {
                const unSelectedSkuKeys: string[] = [];
                const unSelectedTitleFieldsSkuKeys: string[] = [];

                for (let i = 0; i < dataSource.length; i++) {
                    const skuKey = dataSource[i].value;
                    const productSku = productSkuMap.get(skuKey);
                    if (!productSku) continue;

                    unSelectedSkuKeys.push(skuKey);

                    if (enabledTopSellingProductPrinting) {
                        unSelectedTitleFieldsSkuKeys.push(...getSkuPropertiesFieldSkuKeysInHot(productSku));
                    } else {
                        unSelectedTitleFieldsSkuKeys.push(...getSkuPropertiesFieldSkuKeysNotInHot(productSku));
                    }
                }

                nextSkuKeys = difference(nextSkuKeys, unSelectedSkuKeys);
                nextSkuPropertiesFieldSkuKeys = difference(nextSkuPropertiesFieldSkuKeys, unSelectedTitleFieldsSkuKeys);
            } else {
                nextSkuPropertiesFieldSkuKeys = [];
                nextSkuKeys = [];
            }
        }

        orderManagementStore.table.form.setFieldsValue({
            skuPropertiesValue: {
                skuProperties: "",
                skuKey: undefined,
                skuKeys: nextSkuKeys,
                skuPropertiesFieldSkuKeys: nextSkuPropertiesFieldSkuKeys,
            },
        });
    }

    function handleMultipleChange(multiple: boolean) {
        updateSearchSkuText("");
        handleClear();

        if (multiple) {
            localStorage.setItem(SKU_PROPERTIES_FIELD_MULTIPLE, "1");
        } else {
            localStorage.removeItem(SKU_PROPERTIES_FIELD_MULTIPLE);
        }
    }

    function handleSkuSimpleTitleChange(checked: boolean) {
        updateSearchSkuText("");
        handleClear();

        store.updateLoadSkuSimpleTitle(checked);
    }

    /** 非爆款模式下 使用属性set保证sku的合并显示 */
    const uniqSKuPropertiesSet = new Set<string>();

    /** 爆款模式下，使用sku OriginalId+属性 保证sku的合并显示 */
    const uniqHotSkuTagSet = new Set<string>();

    const dataSource: FlexibleSelectData[] = [];

    productSkus?.map((hotProductSku) => {
        const label = hotProductSku.skuProperties;

        /** 无属性，直接忽略 */
        if (!label) return null;

        if (!label.includes(searchSkuText)) return null;

        /** 单/多模式下，已经选择了商品名称，仅展示选中了商品名称下的sku */
        if (orderItemTitleValue.productKey && hotProductSku.productKey !== orderItemTitleValue.productKey) return null;
        if (
            orderItemTitleValue.productKeys &&
            orderItemTitleValue.productKeys.length &&
            !orderItemTitleValue.productKeys.includes(hotProductSku.productKey)
        )
            return null;

        /** 非爆款模式下，相同的属性会被合并 */
        if (!enabledTopSellingProductPrinting && uniqSKuPropertiesSet.has(hotProductSku.skuProperties)) return null;

        /** 爆款模式下，相同Tag的会被合并展示(因为后端有很多originalId相同，属性也相同的Item返回回来) */
        if (enabledTopSellingProductPrinting && uniqHotSkuTagSet.has(buildHotProductTag(hotProductSku))) return null;

        if (!enabledTopSellingProductPrinting) {
            uniqSKuPropertiesSet.add(hotProductSku.skuProperties);
        }

        if (enabledTopSellingProductPrinting) {
            uniqHotSkuTagSet.add(buildHotProductTag(hotProductSku));
        }

        dataSource.push({
            label: (
                <>
                    {enabledTopSellingProductPrinting && hotProductSku.picture ? (
                        <span className="mr5">
                            <ProxyImage width={40} height={40} src={hotProductSku.picture} />
                        </span>
                    ) : null}

                    <span>
                        <Highlight text={label} highlightTexts={[searchSkuText]} />
                    </span>

                    {hotProductSku.count ? <span className="text-error ml5">{hotProductSku.count}件</span> : null}
                </>
            ),
            value: hotProductSku.skuKey,
            inputText: label,
        });
    });

    const initialDataSourceValue = useInitialDataSourceValue(dataSource, searchSkuText);
    const defaultMultiple = !!localStorage.getItem(SKU_PROPERTIES_FIELD_MULTIPLE);

    return (
        <Form.Item name="skuPropertiesValue" label={<span className="search-field-label">商品属性</span>}>
            <FlexibleSelect
                className="sku-properties-select-container"
                data={dataSource}
                inputTextMap={inputTextMap}
                onFocus={handleFocusLoad}
                onChange={handleChange}
                defaultMultiple={defaultMultiple}
                onMultipleChange={handleMultipleChange}
                onSearch={updateSearchSkuText}
                inputValue={skuPropertiesValue.skuProperties}
                singleValue={skuPropertiesValue.skuKey}
                multipleValue={skuPropertiesValue.skuKeys}
                onClear={handleClear}
                shopId={shopId}
                itemHeight={!enabledTopSellingProductPrinting ? 25 + 3 : 44 + 3}
                type={SelectType.SKU_PROPERTIES}
                onEnabledTopSellingProductPrintingChange={store.switchTopSellingProductPrinting}
                enabledTopSellingProductPrinting={store.enabledTopSellingProductPrinting}
                searchText={searchSkuText}
                onSelectAllChange={handleSelectAllChange}
                isIndeterminate={skuPropertiesValue?.isIndeterminate}
                isSelectAll={skuPropertiesValue?.isSelectAll}
                needSkuSimpleTitle={store.loadSkuSimpleTitle}
                onSkuSimpleTitleChange={handleSkuSimpleTitleChange}
            />
        </Form.Item>
    );
});

const SkuPropertiesField: React.FC<SkuPropertiesFieldProps> = function (props) {
    const store = useLinkedProductSearchFieldsStore();
    const { loading } = store;
    const orderItemTitleValue = Form.useWatch("orderItemTitleValue");
    const skuPropertiesValue = Form.useWatch("skuPropertiesValue");

    return (
        <AutoDismissToolTip
            title={
                <a
                    href="https://qw15p3s8hv.feishu.cn/wiki/wikcnopJitfHQXPC15NtGRAxN9c"
                    target="_blank"
                    className="text-white"
                >
                    支持搜索包含多个属性商品的订单，用&分隔，如：红色&XL 点击这里查看详细介绍
                </a>
            }
            trigger="click"
        >
            <Spin spinning={loading}>
                <SkuPropertiesFieldCore
                    {...props}
                    orderItemTitleValue={orderItemTitleValue}
                    skuPropertiesValue={skuPropertiesValue}
                />
            </Spin>
        </AutoDismissToolTip>
    );
};

export default observer(SkuPropertiesField);
